import { Box, Button, Chip, IconButton, MenuItem, Select, Snackbar, TextField, Typography, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { tokens } from '../../../theme';
import EditIcon from '@mui/icons-material/Edit';
import Header from '../../../components/Header';
import ConfirmDeleteDialog from '../../../components/ConfirmDeleteDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DiscountIcon from '@mui/icons-material/Discount';
import { can, filterActionColumns } from '../../../utils/permissions';

const AllPromotions = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastmessage] = useState('');
  const token = localStorage.getItem('token');

  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [departureDateFilter, setDepartureDateFilter] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const getData = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(pageSize));
    if (search) params.set('search', search);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (departureDateFilter) params.set('departureDate', departureDateFilter);

    const res = await fetch(`${process.env.REACT_APP_APILINK}/offers?${params.toString()}`, { headers: { token } });
    const data = await res.json();
    if (data.success) {
      setPromotions(data.offers);
      setTotalCount(data.pagination?.total ?? data.offers.length);
    } else {
      setPromotions([]);
      setTotalCount(0);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search, statusFilter, departureDateFilter]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  // Delete Promotion
  const handleDelete = async (id) => {
    const res = await fetch(`${process.env.REACT_APP_APILINK}/offers/${id}`, { method: 'DELETE', headers: { token } });
    const data = await res.json();
    if (data.message) {
      setOpenToast(true);
      setToastmessage(data.message);
      if (data.success) {
        if (promotions.length === 1 && page > 1) {
          setPage((p) => p - 1);
        } else {
          getData();
        }
      }
    }
  };

  const openDeleteDialog = (id) => {
    setIdToDelete(id);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setIdToDelete(null);
  };

  const confirmDelete = async () => {
    if (idToDelete == null) return;
    await handleDelete(idToDelete);
    closeDeleteDialog();
  };

  const columns = [
    {
      field: 'OfferImage',
      headerName: 'Offer Image',
      filterable: false,
      renderCell: (cellValues) => {
        return (
          <img
            src={cellValues.row.OfferImage}
            style={{ height: '100%', padding: '5px', aspectRatio: '16/9', objectFit: 'cover' }}
            alt="Offer image"
          />
        );
      },
    },
    {
      field: 'OfferName',
      headerName: 'Offer Name',
      flex: 1,
      minWidth: 100,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'OfferDescription',
      headerName: 'Offer Description',
      flex: 1,
      minWidth: 100,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'CountryName',
      headerName: 'Destination',
      flex: 1,
      minWidth: 120,
      cellClassName: 'name-column--cell',
      valueGetter: (params) => params.row.CountryName || 'All trips',
    },
    {
      field: 'departureDate',
      headerName: 'Departure',
      flex: 1,
      minWidth: 100,
      filterable: false,
      cellClassName: 'name-column--cell',
      valueGetter: (params) =>
        params.row.departureDateFormatted || (params.row.departureDate ? params.row.departureDate : '—'),
    },
    {
      field: 'OfferStatus',
      headerName: 'Offer Status',
      flex: 1,
      minWidth: 100,
      filterable: false,
      cellClassName: 'name-column--cell',
      renderCell: (params) => {
        const status = params.value === 1 ? 'Active' : 'In active';
        return <span>{status}</span>;
      },
    },
    {
      field: 'Edit',
      filterable: false,
      renderCell: (cellValues) => {
        return (
          <Link to={`/promotion/${cellValues.row.OfferID}`}>
            <Button
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                ':hover': { backgroundColor: colors.greenAccent[700] },
              }}
              variant="contained"
            >
              <EditIcon />
            </Button>
          </Link>
        );
      },
    },
    {
      field: 'Delete',
      filterable: false,
      renderCell: (cellValues) => {
        return (
          <Button
            sx={{
              backgroundColor: colors.redAccent[500],
              color: colors.grey[100],
              ':hover': { backgroundColor: colors.redAccent[600] },
            }}
            variant="contained"
            onClick={() => openDeleteDialog(cellValues.row.OfferID)}
          >
            <DeleteIcon />
          </Button>
        );
      },
    },
  ];

  // Toast
  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenToast(false);
  };

  const action = (
    <>
      <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseToast}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" gap="10px" flexWrap="wrap">
        <Header title="PROMOTION OFFERS" subtitle="All Promotions" />

        {can('promotions.create') && (
          <Link to={'/promotion/addpromotion'}>
            <Button
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
                fontSize: '14px',
                fontWeight: 'bold',
                padding: '10px 20px',
                '&:hover': {
                  backgroundColor: colors.blueAccent[800],
                },
              }}
            >
              <DiscountIcon sx={{ mr: '10px' }} />
              Add Promotion
            </Button>
          </Link>
        )}
      </Box>

      <Box sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by offer name, description, destination..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: colors.grey[300] }} />,
          }}
          sx={{ minWidth: '300px', backgroundColor: colors.primary[400] }}
        />
        <TextField
          type="date"
          size="small"
          label="Departure Date"
          value={departureDateFilter}
          onChange={(e) => {
            setDepartureDateFilter(e.target.value);
            setPage(1);
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ ml: '20px', minWidth: 200, backgroundColor: colors.primary[400] }}
        />
        <Select
          size="small"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          sx={{ minWidth: 140, backgroundColor: colors.primary[400] }}
        >
          <MenuItem value="all">All Status</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </Select>
      </Box>

      {(search || statusFilter !== 'all' || departureDateFilter) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <Typography variant="body2" sx={{ color: colors.grey[300], fontWeight: 500 }}>
            Active filters:
          </Typography>
          {search && (
            <Chip
              label={`Search: ${search}`}
              size="small"
              onDelete={handleClearSearch}
              sx={{ backgroundColor: colors.blueAccent[700], color: colors.grey[100], '& .MuiChip-deleteIcon': { color: colors.grey[300] } }}
            />
          )}
          {departureDateFilter && (
            <Chip
              label={`Departure Date: ${departureDateFilter}`}
              size="small"
              onDelete={() => {
                setDepartureDateFilter('');
                setPage(1);
              }}
              sx={{ backgroundColor: colors.blueAccent[700], color: colors.grey[100], '& .MuiChip-deleteIcon': { color: colors.grey[300] } }}
            />
          )}
          {statusFilter !== 'all' && (
            <Chip
              label={`Status: ${statusFilter === 'active' ? 'Active' : 'Inactive'}`}
              size="small"
              onDelete={() => {
                setStatusFilter('all');
                setPage(1);
              }}
              sx={{ backgroundColor: colors.blueAccent[700], color: colors.grey[100], '& .MuiChip-deleteIcon': { color: colors.grey[300] } }}
            />
          )}
          {!loading && (
            <Typography variant="body2" sx={{ color: colors.greenAccent[400], ml: 1 }}>
              {totalCount} result{totalCount !== 1 ? 's' : ''}
            </Typography>
          )}
          <Typography
            variant="body2"
            onClick={() => {
              setSearchInput('');
              setSearch('');
              setStatusFilter('all');
              setDepartureDateFilter('');
              setPage(1);
            }}
            sx={{
              color: colors.redAccent[500],
              cursor: 'pointer',
              ml: 1,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Clear all
          </Typography>
        </Box>
      )}

      <Box
        m="10px 0 0 0"
        height="667px"
        sx={{
          '& .MuiDataGrid-root': {
            border: 'none',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: 'none',
          },
          '& .name-column--cell': {
            color: colors.grey[100],
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: colors.blueAccent[700],
            borderBottom: 'none',
          },
          '& .MuiDataGrid-virtualScroller': {
            backgroundColor: colors.primary[400],
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'none',
            backgroundColor: colors.blueAccent[700],
          },
          '& .MuiCheckbox-root': {
            color: `${colors.greenAccent[200]} !important`,
          },
          '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
            color: `${colors.grey[100]} !important`,
            fontSize: 14,
          },
        }}
      >
        <DataGrid
          rows={promotions}
          columns={filterActionColumns(columns, { Edit: 'promotions.edit', Delete: 'promotions.delete' })}
          getRowId={(row) => row.OfferID}
          disableColumnFilter
          loading={loading}
          pagination
          paginationMode="server"
          page={page - 1}
          pageSize={pageSize}
          rowCount={totalCount}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(newPage) => setPage(newPage + 1)}
          onPageSizeChange={(newPageSize) => {
            setPageSize(newPageSize);
            setPage(1);
          }}
        />
      </Box>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        title="Delete promotion?"
        message="Are you sure you want to delete this promotion?"
        colors={colors}
      />

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        message={toastMessage}
        action={action}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default AllPromotions;
